import type { Grossbild } from '../api/types'
import { istAppleMitFinger } from './geraet'

/**
 * Ein Foto aus der Anwendung heraus sichern — **ein Knopf, zwei Systeme**.
 *
 * **Warum das nicht der Browser erledigt.** Auf iOS legt ein langes Drücken
 * das Bild in „Fotos"; auf Android passiert dabei nichts (#58). Eine Geste,
 * die auf dem einen Gerät etwas tut und auf dem anderen nicht, ist keine
 * Bedienung, auf die sich eine Anwendung stützen kann.
 *
 * **Und der erste Anlauf war trotzdem falsch.** Er fragte überall zuerst das
 * Teilen-Blatt (`navigator.share`), weil beide Systeme es können. Aus dem
 * Betrieb kam die Rückmeldung von Android-Nutzern: dort steht darin nur
 * „Speichern in Google Bilder" — die Samsung Gallery taucht nicht auf.
 *
 * **Der Grund ist ein Unterschied der Systeme und kein Fehler hier.** Androids
 * Teilen-Blatt reicht Dateien an *Apps* weiter, die sich als Empfänger
 * eingetragen haben; ein „auf dem Gerät sichern" kennt es gar nicht. Was dort
 * wirklich in jeder Galerie auftaucht, ist der Ordner **Downloads** — und
 * dorthin kommt eine Datei über einen gewöhnlichen Download. Auf iOS ist es
 * genau andersherum: das Teilen-Blatt hat „Bild sichern" und legt es in
 * *Fotos*, ein Download landet in *Dateien*.
 *
 * **Also entscheidet nicht die Fähigkeit, sondern das System** — und das ist
 * hier ausnahmsweise richtig so: beide *können* teilen, sie tun damit nur
 * Verschiedenes. Eine Abfrage der Fähigkeit könnte das nicht auseinanderhalten
 * (`lib/geraet.ts` erklärt, warum diese Ausnahme eine ist).
 *
 *  - **iPhone und iPad:** das Teilen-Blatt. Dort heißt „Sichern" wirklich
 *    sichern.
 *  - **Alles andere** (Android, jeder Rechner): herunterladen. Auf Android
 *    landet es in *Downloads*, und den zeigt jede Galerie-App als Album.
 *
 * **Zwei Knöpfe wären die schlechtere Antwort gewesen** — aus dem Betrieb, und
 * knapp: „ich will nicht 2 Buttons - das verwirrt ja nur". Stimmt: wer ein
 * Foto sichern will, will nicht vorher wissen müssen, wie sein System das
 * intern nennt.
 *
 * **Geholt wird über dieselbe Adresse wie das angezeigte Bild**, also durch
 * `Visibility` hindurch. Das ist kein Umweg, sondern der Punkt: es gibt keinen
 * zweiten Weg zu einer Datei, und dieser hier bekommt keinen.
 */
export type Weg = 'geteilt' | 'geladen' | 'abgebrochen'

/**
 * Whether saving needs a gesture of its own (1.53.0).
 *
 * The share sheet opens only inside a user activation: iOS refuses
 * `navigator.share()` a moment after the tap, with `NotAllowedError` -- the
 * same rule as for a passkey (`CLAUDE.md`). A download (`<a download>`)
 * does not care. So whoever wants to hand a file to the share sheet **after
 * a long call** (the book is built for minutes, the ZIP is packed) does not
 * call `sichereDatei()` at the end of that call; the tap that started it is
 * long gone. A button stands there instead, and its own tap fetches and
 * shares. From use: "der Download von PDF und epub funktioniert nur im
 * Browser, aber nicht in der WebApp" -- the share attempt failed silently
 * and the download that followed does nothing in the home-screen app.
 *
 * Where the share sheet is the way is decided by the system, in one place
 * (`istAppleMitFinger`, `lib/geraet.ts`); everywhere else the download is
 * the way, and that one may follow a long call without a gesture.
 */
export function brauchtEigeneGeste(): boolean {
  return istAppleMitFinger()
}

export async function sichereFoto(media: Grossbild): Promise<Weg> {
  return sichereDatei(media.url, dateiname(media), media.mime)
}

/**
 * Eine Datei von einer Adresse der Anwendung sichern -- derselbe Weg wie beim
 * Foto, seit 0.30.6 auch für das Buch (PDF, EPUB), seit 1.53.0 für das
 * Medien-ZIP. Aus dem Betrieb: „der
 * Download von PDF und epub funktioniert nur im Browser, aber nicht in der
 * WebApp." Ein `<a download>` tut in der vom Home-Bildschirm gestarteten
 * App auf iOS nichts; das Teilen-Blatt bietet dort „In Dateien sichern" und
 * „Bücher" an.
 *
 * **The gesture is the caller's business** (`brauchtEigeneGeste()`): this
 * function fetches and then shares, and the share sheet opens only inside
 * the tap that called it. Called at the end of a long build, it falls back
 * to the download without a word -- that is the fault this note is about.
 *
 * **The whole file goes into memory** (`blob()`): a `File` for the share
 * sheet is a thing, not a stream. For a media ZIP that can be gigabytes;
 * decided from use without a limit, and the link "… öffnen" stays next to
 * the button for the case that the tab does not survive it.
 */
export async function sichereDatei(url: string, name: string, mime: string): Promise<Weg> {
  return handOver(await fetchFile(url, name, mime))
}

/**
 * The file behind an address of the app, as a `File` (1.159.0).
 *
 * Split from handing it on because a video takes longer to fetch than iOS
 * keeps a tap alive: `saveMedia()` fetches first and lets a second tap hand
 * it on (`brauchtEigeneGeste()`).
 */
export async function fetchFile(
  url: string,
  name: string,
  mime: string,
  laden: { signal?: AbortSignal; onBytes?: (geladen: number, gesamt: number | null) => void } = {},
): Promise<File> {
  // Same-Origin: das Session-Cookie geht von selbst mit. Und ein Bild steht
  // schon im Zwischenspeicher des Browsers -- es wird gerade angezeigt --,
  // deshalb ist das im Regelfall keine zweite Übertragung.
  const antwort = await fetch(url, { signal: laden.signal })
  if (!antwort.ok) throw new Error(`Der Server antwortete mit ${antwort.status}.`)
  // **Read in pieces when someone watches** (1.160.0): a bar needs the bytes
  // as they come, and `blob()` says nothing until the end. Without a reader
  // (an old browser) it is the whole file at once, as before.
  const leser = laden.onBytes === undefined ? undefined : antwort.body?.getReader()
  if (leser === undefined) {
    const inhalt = await antwort.blob()
    return new File([inhalt], name, { type: inhalt.type || mime })
  }
  const laenge = Number(antwort.headers.get('content-length'))
  const gesamt = Number.isFinite(laenge) && laenge > 0 ? laenge : null
  const stuecke: Uint8Array<ArrayBuffer>[] = []
  let geladen = 0
  laden.onBytes?.(0, gesamt)
  for (;;) {
    const { done, value } = await leser.read()
    if (done) break
    stuecke.push(value as Uint8Array<ArrayBuffer>)
    geladen += value.byteLength
    laden.onBytes?.(geladen, gesamt)
  }
  const typ = antwort.headers.get('content-type') ?? ''
  return new File(stuecke, name, { type: typ !== '' ? typ.split(';')[0] ?? mime : mime })
}

/**
 * Hand a fetched file to the system: the share sheet on iPhone and iPad,
 * a download everywhere else. **`navigator.share()` is the first call**, no
 * `await` before it, so a caller inside a tap keeps the tap.
 */
export async function handOver(datei: File): Promise<Weg> {
  return handOverAll([datei])
}

/**
 * Several files at once (1.159.0): one share sheet with all of them on
 * iPhone and iPad -- "N Objekte sichern" puts them into Photos together --,
 * one download each elsewhere.
 */
export async function handOverAll(dateien: File[]): Promise<Weg> {
  /*
   * **Erst das System, dann die Fähigkeit.** `canShare` allein genügt nicht:
   * Android kann es auch, und dort ist es der falsche Weg. Und umgekehrt darf
   * die Fähigkeit nicht fehlen — ein altes iOS ohne Dateifreigabe bekommt den
   * Download, statt in eine Ausnahme zu laufen.
   */
  if (istAppleMitFinger() && navigator.canShare?.({ files: dateien }) === true) {
    try {
      await navigator.share({ files: dateien })
      return 'geteilt'
    } catch (fehler) {
      // Wer das Blatt zumacht, hat nicht gescheitert -- er wollte nicht.
      if (abgebrochen(fehler)) return 'abgebrochen'
      // Alles andere ist ein echter Fehlschlag des einen Weges; dann bleibt
      // der andere. Eine Fehlermeldung, wo ein Download getan hätte, wäre
      // eine stille Absage mit Text.
    }
  }

  for (const [nummer, datei] of dateien.entries()) {
    // A short pause between two: Chrome drops a download that follows the
    // one before within the same moment.
    if (nummer > 0) await new Promise((weiter) => setTimeout(weiter, 300))
    const adresse = URL.createObjectURL(datei)
    const verweis = document.createElement('a')
    verweis.href = adresse
    verweis.download = datei.name
    document.body.append(verweis)
    verweis.click()
    verweis.remove()
    // Erst freigeben, wenn der Browser den Download angefasst hat. Sofort
    // widerrufen heisst auf manchen Geräten: leere Datei.
    setTimeout(() => URL.revokeObjectURL(adresse), 60_000)
  }
  return 'geladen'
}

/**
 * „reisefoto-42.jpg" — ein Name, den man wiederfindet.
 *
 * Der ursprüngliche Dateiname liegt nirgends: hochgeladen wird ein Bild, das
 * der Browser vorher verkleinert hat, und `IMG_4711.HEIC` beschreibt danach
 * ohnehin etwas anderes. Die Nummer ist dafür eindeutig, und die Endung kommt
 * aus dem Typ, den der Server nennt.
 */
export function dateiname(media: Grossbild): string {
  // A video is not a photo (1.159.0): `reisevideo-42.mp4`.
  const art = media.mime.toLowerCase().startsWith('video/') ? 'reisevideo' : 'reisefoto'
  return `${art}-${media.id}.${endung(media.mime)}`
}

const ENDUNGEN: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
}

function endung(mime: string): string {
  const bekannt = ENDUNGEN[mime.toLowerCase()]
  if (bekannt !== undefined) return bekannt
  // `image/etwas-neues` -- der hintere Teil ist als Endung besser als „bin".
  const hinten = mime.split('/')[1]?.replace(/[^a-z0-9]/gi, '') ?? ''
  return hinten === '' ? 'jpg' : hinten.toLowerCase()
}

/** Das Teilen-Blatt zugemacht — kein Fehler, sondern eine Entscheidung. */
function abgebrochen(fehler: unknown): boolean {
  return fehler instanceof DOMException && fehler.name === 'AbortError'
}
